import { useCallback } from "react";

import { useMockableLocation } from "./use-mockable-location";
import { type QueryParamNavOptions, useMockableNavigate } from "./use-mockable-navigate";

import { store, useAppDispatch, useAppSelector } from "store";
import { selectQueryString } from "store/selectors/view";
import { viewStateActions } from "store/slices/view";

/**
 * Read a query param from the Redux store without a React subscription.
 * Use in event handlers / imperative code to avoid re-renders from query changes.
 */
export const getQueryParamFromStore = (key: string): string | null =>
  new URLSearchParams(selectQueryString(store.getState())).get(key);

/**
 * Mutate query params without subscribing to the query string.
 * Prefer this when a component only sets/deletes params.
 */
export const useQueryParamsActions = () => {
  const dispatch = useAppDispatch();
  const getLocation = useMockableLocation();
  const goTo = useMockableNavigate();

  const _update = useCallback(
    (
      action: "set" | "delete",
      params: { [key: string]: string | null },
      options?: QueryParamNavOptions,
    ) => {
      const location = getLocation();
      const newParams = new URLSearchParams(location.search);

      for (const [key, value] of Object.entries(params)) {
        if (action === "set") {
          if (value) {
            newParams.set(key, value);
          } else if (value === null) {
            newParams.delete(key);
          } else {
            // ignore
          }
        } else if (action === "delete") {
          if (value) {
            newParams.delete(key, value);
          } else {
            newParams.delete(key);
          }
        }
      }

      goTo(newParams, location, options);
      dispatch(viewStateActions.updateQueryParams(newParams.toString()));
    },
    [dispatch, getLocation, goTo],
  );

  /**
   * Set query params
   * @param params - An object with the key and value to set
   */
  const setQueryParams = useCallback(
    (params: { [key: string]: string | null }, options?: QueryParamNavOptions) =>
      _update("set", params, options),
    [_update],
  );

  /**
   * Delete query params
   * @param params - An array of strings or arrays with the key and specific value to delete
   */
  const deleteQueryParams = useCallback(
    (params: string[] | [string, string][], options?: QueryParamNavOptions) => {
      const paramsToRemove: Record<string, string | null> = {};
      for (const key of params) {
        if (Array.isArray(key)) {
          paramsToRemove[key[0]] = key[1];
        } else {
          paramsToRemove[key] = null;
        }
      }
      _update("delete", paramsToRemove, options);
    },
    [_update],
  );

  return {
    setQueryParams,
    deleteQueryParams,
  };
};

/**
 * Subscribe to a single query param. Re-renders only when that key's value changes,
 * not when unrelated query params change.
 */
export const useQueryParam = (key: string): string | null => {
  return useAppSelector((state) => new URLSearchParams(selectQueryString(state)).get(key));
};

/**
 * Subscribe to whether a query param is truthy ("true", "1", or empty string).
 * Re-renders only when that key's truthiness changes.
 */
export const useIsQueryParamTruthy = (key: string): boolean => {
  return useAppSelector((state) => {
    const queryParams = new URLSearchParams(selectQueryString(state));
    if (!queryParams.has(key)) {
      return false;
    }
    const value = queryParams.get(key);
    // Empty strings are truthy for params set with no value (e.g. "?skip-av-check")
    return value === "true" || value === "1" || value === "";
  });
};
